// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { defineMessages } from 'react-intl'
import classnames from 'classnames'

import tts from '@console/api/tts'

import { IconCopy, IconCopyCheck } from '@ttn-lw/components/icon'
import Toggle from '@ttn-lw/components/panel/toggle'
import Button from '@ttn-lw/components/button'

import Message from '@ttn-lw/lib/components/message'

import PropTypes from '@ttn-lw/lib/prop-types'
import { selectAsConfig } from '@ttn-lw/lib/selectors/env'
import sharedMessages from '@ttn-lw/lib/shared-messages'

import style from './message-storage-commands.tti.styl'

const m = defineMessages({
  title: 'Run the command to download all messages:',
  curl: 'cURL',
  cli: 'CLI',
  learnMore: 'Learn more about fetching data in our dedicated <Link>documentation →</Link>',
})

const MessageStorageCommands = ({ appId, date, devId }) => {
  const asUri = selectAsConfig().base_url

  const toggleOptions = [
    {
      label: m.curl,
      value: 'curl',
    },
    {
      label: m.cli,
      value: 'cli',
    },
  ]
  const [activeToggle, setActiveToggle] = useState(toggleOptions[0].value)
  const [copied, setCopied] = useState(false)
  const [apiKey, setApiKey] = useState('$API_KEY')
  const [loading, setLoading] = useState(false)

  const _timer = useRef(null)

  useEffect(
    () => () => {
      clearTimeout(_timer.current)
    },
    [],
  )

  const handleToggleChange = useCallback((_, value) => {
    setActiveToggle(value)
  }, [])

  const canCopy = navigator.clipboard && navigator.clipboard.writeText

  const command = useMemo(() => {
    if (activeToggle === 'curl') {
      if (!Boolean(devId)) {
        return `curl -G "${asUri}/as/applications/${appId}/packages/storage/uplink_message" \\
     -H "Authorization: Bearer ${apiKey}" \\
     -H "Accept: text/event-stream" \\
     -d "last=${date}"`
      }
      return `curl -G "${asUri}/as/applications/${appId}/devices/${devId}/packages/storage/uplink_message" \\
     -H "Authorization: Bearer ${apiKey}" \\
     -H "Accept: text/event-stream" \\
     -d "last=${date}"`
    }
    if (!Boolean(devId)) {
      return `ttn-lw-cli applications storage get ${appId} --last "${date}"`
    }
    return `ttn-lw-cli end-devices storage get ${appId} ${devId} --last "${date}"`
  }, [activeToggle, apiKey, appId, asUri, date, devId])

  const handleCopyClick = useCallback(() => {
    if (copied) {
      return
    }
    if (canCopy) {
      navigator.clipboard.writeText(command)
      setCopied(true)

      _timer.current = setTimeout(() => {
        setCopied(false)
      }, 3000)
    }
  }, [canCopy, copied, command])

  const createAPIKeyGenerator = description => async () => {
    const key = {
      name: `message-storage-${Date.now()}`,
      rights: description.rights,
    }
    setLoading(true)
    const result = await tts.Applications.ApiKeys.create(appId, key)
    setLoading(false)

    description.set(result.key)
  }

  const handleGenerateAPIKey = useCallback(
    createAPIKeyGenerator({
      rights: ['RIGHT_APPLICATION_TRAFFIC_READ'],
      set: setApiKey,
    }),
    [appId],
  )

  return (
    <div className="d-flex flex-column gap-ls-xs">
      <Message component="h2" content={m.title} className="m-0" />
      <div className={style.commandsWrapper}>
        <div className="d-flex j-between">
          <div className="d-flex gap-cs-m al-center">
            <Toggle
              options={toggleOptions}
              active={activeToggle}
              onToggleChange={handleToggleChange}
            />
            {activeToggle === 'curl' && (
              <Button
                secondary
                message={sharedMessages.generateApiKey}
                onClick={handleGenerateAPIKey}
                disabled={apiKey !== '$API_KEY' || loading}
              />
            )}
          </div>

          {canCopy && (
            <Button
              icon={copied ? IconCopyCheck : IconCopy}
              className={classnames(style.maximize, copied && 'c-icon-success-normal')}
              data-clipboard-text={command}
              onClick={handleCopyClick}
              tooltip={copied ? sharedMessages.copiedToClipboard : sharedMessages.copy}
              naked
              small
            />
          )}
        </div>
        <div>
          <code className={style.command}>{command}</code>
        </div>
      </div>
    </div>
  )
}

MessageStorageCommands.propTypes = {
  appId: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  devId: PropTypes.string,
}

MessageStorageCommands.defaultProps = {
  devId: undefined,
}

export default MessageStorageCommands
